import Button from '@/components/core/Button';
import EntityState from '@/se/components/EntityState';
import ErrorFactory from '@/se/components/errors/Error';
import LinkButton from '@/se/components/LinkButton';
import { isDefinedAndNotNull } from '@/se/utilities/check';
import { Mutation, Query } from '@apollo/client/react/components';
import Box from '@material-ui/core/Box';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import React, { Fragment } from 'react';
import scheme from '../../../graph/transitionRules';
import RoomTypeSelectInput from './RoomTypeSelectInput';
import TimeoutInput from './TimeoutInput';

const Error = ErrorFactory();

const EmptyStateContainer = ({ data, children }) => {
  if (isDefinedAndNotNull(data) && data.length > 0) {
    return children;
  } else {
    return (
      <EntityState
        title={'No Rules Configured.'}
        hint={`System regard any bracelet transition as valid until there is at least one rule.`}
      />
    );
  }
};

const useStyles = theme => ({
  ruleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
});

const getHash = (values = []) => values.reduce((acc, _) => `${acc}|${_.fromRoom}${_.toRoom}${_.timeoutSeconds}|`, '');
const sanitizeVariables = items =>
  items.map(({ fromRoom, toRoom, timeoutSeconds }) => ({ fromRoom, toRoom, timeoutSeconds }));

class ListForm extends React.PureComponent {
  state = {
    values: [],
    initialHash: '',
    initialValues: [],
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    const initialValues = nextProps.initialValues;
    if (getHash(prevState.initialValues) !== getHash(initialValues)) {
      return {
        ...prevState,
        initialValues,
        initialHash: getHash(initialValues),
        values: initialValues || [],
      };
    }
    return null;
  }

  onSubmit = mutation => e => {
    e.preventDefault();
    mutation({
      variables: {
        transitionRules: sanitizeVariables(this.state.values),
      },
      refetchQueries: [{ query: scheme.list }],
    });
  };

  updateCompleted = items => {
    if (items) {
      this.setState({
        values: items.batchUpdateTransitionRule,
        initialValues: [],
      });
    }
  };

  removeItem = idx => () => {
    const values = [...this.state.values];
    values.splice(idx, 1);
    this.setState({ values: values });
  };

  addItem = () => {
    const emptyValue = {
      fromRoom: null,
      toRoom: null,
      timeoutSeconds: 0,
    };

    this.setState({ values: [...this.state.values, emptyValue] });
  };

  itemChange = (id, field) => data => {
    const values = [...this.state.values];
    values[id][field] = data;
    this.setState({ values: values });
  };

  render() {
    const { values, initialHash } = this.state;
    const { classes } = this.props;
    const isDirty = initialHash !== getHash(values);

    return (
      <Mutation mutation={scheme.batchUpdate} onCompleted={this.updateCompleted}>
        {(updateRules, { loading, error }) => (
          <form onSubmit={this.onSubmit(updateRules)}>
            <EmptyStateContainer data={values}>
              <>
                <Box mb={4}>
                  <Typography>
                    Each row represents one valid transition, all others are considered invalid and ignored.
                  </Typography>
                  <Typography>
                    Timeout in seconds represents the time that needs to pass before a transition is triggered (0 means
                    immediately).
                  </Typography>
                </Box>

                {values.map(({ fromRoom, toRoom, timeoutSeconds }, index) => (
                  <div className={classes.ruleRow} key={index}>
                    <Box flex={1}>
                      <RoomTypeSelectInput
                        label="Transition From"
                        value={fromRoom}
                        onChange={this.itemChange(index, 'fromRoom')}
                      />
                    </Box>
                    <Box flex={1}>
                      <RoomTypeSelectInput
                        label="Transition To"
                        value={toRoom}
                        onChange={this.itemChange(index, 'toRoom')}
                      />
                    </Box>
                    <Box flex={1}>
                      <TimeoutInput value={timeoutSeconds} onChange={this.itemChange(index, 'timeoutSeconds')} />
                    </Box>
                    <div>
                      <Button variant="text" onClick={this.removeItem(index)}>
                        Remove
                      </Button>
                    </div>
                  </div>
                ))}
              </>
            </EmptyStateContainer>

            <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
              <LinkButton type="button" onClick={this.addItem} busy={loading}>
                Add New Rule Set
              </LinkButton>
              {isDirty && <LinkButton busy={loading}>Save Changes</LinkButton>}
            </Box>

            <Error isVisible={!!error} id="TransitionRulesFormError">
              <Typography>An unexpected error has occurred. Please, try again or contact our support.</Typography>
            </Error>
          </form>
        )}
      </Mutation>
    );
  }
}

const StyledListForm = withStyles(useStyles)(ListForm);

const TransitionRules = props => (
  <Query query={scheme.list}>
    {({ loading, error, data = {} }) => <StyledListForm initialValues={data.transitionRules} />}
  </Query>
);

export default TransitionRules;
